import { ChangeDetectionStrategy, Component, computed, inject, signal, DestroyRef, SecurityContext } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, AbstractControl, ValidationErrors } from '@angular/forms';

import { DomSanitizer } from '@angular/platform-browser';
import { ThemingService } from '@shared/services/theming.service';
import { BrandingService, BrandingConfiguration } from '@shared/services/branding.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-branding-editor',
  templateUrl: './branding-editor.component.html',
  styleUrls: ['./branding-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule]
})
export class BrandingEditorComponent {
  private readonly formBuilder = inject(FormBuilder);
  private readonly themingService = inject(ThemingService);
  private readonly brandingService = inject(BrandingService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly destroyRef = inject(DestroyRef);

  // Signals for reactive state management
  public readonly isLoading = signal(false);
  public readonly isSaving = signal(false);
  public readonly statusMessage = signal<string>('');
  public readonly isSuccessStatus = computed(() => this.statusMessage().toLowerCase().includes('success'));
  public readonly isErrorStatus = computed(() => {
    const msg = this.statusMessage().toLowerCase();
    return msg.includes('error') || msg.includes('invalid') || msg.includes('failed') || msg.includes('cannot') || msg.includes('exceeds');
  });
  public readonly hasUnsavedChanges = signal(false);
  public readonly faviconFileName = signal<string>('');
  public readonly logoFileName = signal<string>('');
  public readonly faviconUrl = signal<string>('');
  public readonly logoUrl = signal<string>('');
  public readonly loginBackgroundImageFileName = signal<string>('');
  public readonly loginBackgroundImageUrl = signal<string>('');

  // Form group for branding configuration
  public brandingForm!: FormGroup;

  // File upload properties
  private faviconFile: File | null = null;
  private logoFile: File | null = null;
  private loginBackgroundImageFile: File | null = null;

  constructor() {
    this.initializeForm();
    this.loadCurrentBrandingConfig();
    this.setupFormChangeDetection();
  }

  private initializeForm(): void {
    this.brandingForm = this.formBuilder.group({
      themeColour: ['#2563eb', [Validators.required, Validators.pattern(/^#[0-9A-Fa-f]{6}$/), this.themeColorValidator.bind(this)]],
      fontFamily: ['Lato, sans-serif', [Validators.required, this.fontFamilyValidator.bind(this)]],
      fontColour: ['#2c3e5a', [Validators.required, Validators.pattern(/^#[0-9A-Fa-f]{6}$/), this.textColorValidator.bind(this)]],
    });
  }

  private loadCurrentBrandingConfig(): void {
    this.isLoading.set(true);

    this.brandingService.getBrandingConfiguration()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (config) => {
          this.brandingForm.patchValue(config, { emitEvent: false });

          // Load favicon and logo URLs from database
          if (config.faviconUrl) {
            this.faviconUrl.set(config.faviconUrl);
          }
          if (config.logoUrl) {
            this.logoUrl.set(config.logoUrl);
          }
          if (config.loginBackgroundImageUrl) {
            this.loginBackgroundImageUrl.set(config.loginBackgroundImageUrl);
          }

          this.isLoading.set(false);
        },
        error: (error) => {
          console.error('Error loading branding configuration:', error);
          this.statusMessage.set('Error loading current configuration. Using defaults.');
          this.isLoading.set(false);
        }
      });
  }

  public cancelChanges(): void {
    // Clear any pending file uploads
    this.clearUploadedFiles();
    this.faviconFileName.set('');
    this.logoFileName.set('');
    this.loginBackgroundImageFileName.set('');

    // Clear status message
    this.statusMessage.set('');

    // Reload the original configuration from the server
    this.loadCurrentBrandingConfig();

    // Reset form state
    this.brandingForm.markAsPristine();
    this.hasUnsavedChanges.set(false);
  }

  private patchFormWithConfig(config: BrandingConfiguration): void {
    this.brandingForm.patchValue({
      themeColour: config.themeColour ?? '#2563eb',
      fontFamily: config.fontFamily ?? 'Lato, sans-serif',
      fontColour: config.fontColour ?? '#2c3e5a'
    }, { emitEvent: false });

    this.applyPreview();
  }

  public onThemeColourPickerInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    this.brandingForm.get('themeColour')?.setValue(value, { emitEvent: true, onlySelf: true });
  }

  public onFontColourPickerInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    this.brandingForm.get('fontColour')?.setValue(value, { emitEvent: true, onlySelf: true });
  }

  private setupFormChangeDetection(): void {
    this.brandingForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.hasUnsavedChanges.set(this.brandingForm.dirty);
        this.applyPreview();
        this.updateFormValidationMessage();
      });
  }

  private updateFormValidationMessage(): void {
    // Only show validation messages if the form has been touched
    if (!this.brandingForm.dirty) {
      return;
    }

    const errors: string[] = [];

    Object.keys(this.brandingForm.controls).forEach(key => {
      const control = this.brandingForm.get(key);
      if (control && control.invalid && control.touched) {
        const error = this.getFormControlError(key);
        if (error) {
          errors.push(`${this.getFieldLabel(key)}: ${error}`);
        }
      }
    });

    if (errors.length > 0) {
      this.statusMessage.set(`Invalid format. ${errors.join('; ')}`);
    } else if (this.statusMessage().toLowerCase().includes('invalid format')) {
      this.statusMessage.set(''); // Clear validation errors when fixed
    }
  }

  private getFieldLabel(controlName: string): string {
    const labels: { [key: string]: string } = {
      'themeColour': 'Theme Colour',
      'fontFamily': 'Font Family',
      'fontColour': 'Text Colour'
    };
    return labels[controlName] || controlName;
  }

  public markFieldAsTouched(fieldName: string): void {
    const control = this.brandingForm.get(fieldName);
    if (control) {
      control.markAsTouched();
      this.updateFormValidationMessage();
    }
  }

  public selectPredefinedColor(color: string): void {
    this.brandingForm.patchValue({ themeColour: color });
  }

  /**
   * Custom validator for theme color input using Angular's DomSanitizer
   */
  private themeColorValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }

    const color = control.value.trim();

    // Use Angular's DomSanitizer to sanitize the color value
    const sanitized = this.sanitizer.sanitize(SecurityContext.STYLE, color);

    // If sanitizer returns null or empty string, the value contains dangerous content
    if (!sanitized || sanitized.trim() === '') {
      return { invalidColor: { message: 'Theme color contains invalid or dangerous characters' } };
    }

    // Check if sanitized value differs from original (indicates removed content)
    if (sanitized.trim() !== color.trim()) {
      return { invalidColor: { message: 'Theme color contains potentially unsafe content' } };
    }

    return null;
  }

  /**
   * Custom validator for text color input using Angular's DomSanitizer
   */
  private textColorValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }

    const color = control.value.trim();

    // Use Angular's DomSanitizer to sanitize the color value
    const sanitized = this.sanitizer.sanitize(SecurityContext.STYLE, color);

    // If sanitizer returns null or empty string, the value contains dangerous content
    if (!sanitized || sanitized.trim() === '') {
      return { invalidColor: { message: 'Text color contains invalid or dangerous characters' } };
    }

    // Check if sanitized value differs from original (indicates removed content)
    if (sanitized.trim() !== color.trim()) {
      return { invalidColor: { message: 'Text color contains potentially unsafe content' } };
    }

    return null;
  }

  /**
   * Custom validator for font family input using Angular's DomSanitizer
   */
  private fontFamilyValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }

    const fontFamily = control.value.trim();

    // Check length
    if (fontFamily.length > 100) {
      return { maxLength: { actualLength: fontFamily.length, requiredLength: 100 } };
    }

    // Use Angular's DomSanitizer to sanitize the font family value
    const sanitized = this.sanitizer.sanitize(SecurityContext.STYLE, fontFamily);

    // If sanitizer returns null or empty string, the value contains dangerous content
    if (!sanitized || sanitized.trim() === '') {
      return { invalidFontFamily: { message: 'Font family contains invalid or dangerous characters' } };
    }

    // Check if sanitized value differs significantly from original (indicates removed content)
    if (sanitized.trim() !== fontFamily.trim()) {
      return { invalidFontFamily: { message: 'Font family contains potentially unsafe content' } };
    }

    return null;
  }

  /**
   * Get sanitized font family for safe display in preview
   */
  public getSanitizedFontFamily(): string {
    const fontFamily = this.brandingForm.get('fontFamily')?.value;
    if (!fontFamily) {
      return 'Lato, sans-serif';
    }

    // Sanitize the font family value
    const sanitized = this.sanitizer.sanitize(SecurityContext.STYLE, fontFamily);
    return sanitized || 'Lato, sans-serif';
  }

  public applyPreview(): void {
    if (this.brandingForm.valid) {
      const formValue = this.brandingForm.value;

      // Apply theme preview using the theming service
      document.body.style.setProperty('--theme-colour', formValue.themeColour);
      document.body.style.setProperty('--font-family', formValue.fontFamily);
      document.body.style.setProperty('--font-colour', formValue.fontColour);
    }
  }

  public resetToDefaults(): void {
    this.brandingService.resetBrandingConfiguration()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.brandingForm.patchValue(response.data, { emitEvent: false });
            this.brandingService.applyBrandingConfiguration(response.data);
            this.statusMessage.set('Reset to default branding configuration.');
          } else {
            this.statusMessage.set('Failed to reset branding configuration.');
          }
        },
        error: (error) => {
          console.error('Error resetting branding configuration:', error);
          this.statusMessage.set('Error resetting branding configuration.');
        }
      });
  }

  private readonly FAVICON_ALLOWED_FILE_TYPES = ['image/x-icon', 'image/vnd.microsoft.icon'];
  private readonly FAVICON_ALLOWED_EXTENSIONS = ['.ico'];
  private readonly IMAGE_ALLOWED_FILE_TYPES = ['image/png', 'image/jpeg'];
  private readonly IMAGE_ALLOWED_EXTENSIONS = ['.png', '.jpg', '.jpeg'];
  private readonly MAX_FILE_SIZE = 1 * 1024 * 1024; // 1 MB in bytes

  private validateFile(file: File, type: string, isFavicon = false): string | null {
    const allowedTypes = isFavicon ? this.FAVICON_ALLOWED_FILE_TYPES : this.IMAGE_ALLOWED_FILE_TYPES;
    const allowedExts = isFavicon ? this.FAVICON_ALLOWED_EXTENSIONS : this.IMAGE_ALLOWED_EXTENSIONS;
    const formatLabel = isFavicon ? 'ICO' : 'PNG and JPG/JPEG';
    const extLabel = isFavicon ? '.ico' : '.png, .jpg, and .jpeg';

    // Check file type
    if (!allowedTypes.includes(file.type)) {
      return `Invalid file type for ${type}. Only ${formatLabel} are allowed.`;
    }

    // Check file extension
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!allowedExts.includes(fileExtension)) {
      return `Invalid file extension for ${type}. Only ${extLabel} are allowed.`;
    }

    // Check file size
    if (file.size > this.MAX_FILE_SIZE) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
      return `File size (${sizeMB}MB) exceeds maximum allowed size of 1MB for ${type}.`;
    }

    return null;
  }

  public onFaviconSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      // Validate file (favicon requires ICO only)
      const validationError = this.validateFile(file, 'favicon', true);
      if (validationError) {
        this.statusMessage.set(validationError);
        input.value = ''; // Clear the input
        return;
      }

      this.faviconFile = file;
      this.faviconFileName.set(file.name);
      this.previewFile(file, 'favicon');
      this.statusMessage.set(''); // Clear any previous error messages
      this.brandingForm.markAsDirty();
      this.hasUnsavedChanges.set(true);
    }
  }

  public onLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      // Validate file
      const validationError = this.validateFile(file, 'logo');
      if (validationError) {
        this.statusMessage.set(validationError);
        input.value = ''; // Clear the input
        return;
      }

      this.logoFile = file;
      this.logoFileName.set(file.name);
      this.previewFile(file, 'logo');
      this.statusMessage.set(''); // Clear any previous error messages
      this.brandingForm.markAsDirty();
      this.hasUnsavedChanges.set(true);
    }
  }

  public clearFavicon(): void {
    this.faviconFile = null;
    this.faviconFileName.set('');
    this.faviconUrl.set('');
    this.brandingForm.markAsDirty();
    this.hasUnsavedChanges.set(true);
  }

  public clearLogo(): void {
    this.logoFile = null;
    this.logoFileName.set('');
    this.logoUrl.set('');
    this.brandingForm.markAsDirty();
    this.hasUnsavedChanges.set(true);
  }

  public onLoginBackgroundImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      // Validate file
      const validationError = this.validateFile(file, 'login background image');
      if (validationError) {
        this.statusMessage.set(validationError);
        input.value = ''; // Clear the input
        return;
      }

      this.loginBackgroundImageFile = file;
      this.loginBackgroundImageFileName.set(file.name);
      this.previewFile(file, 'loginBackgroundImage');
      this.statusMessage.set(''); // Clear any previous error messages
      this.brandingForm.markAsDirty();
      this.hasUnsavedChanges.set(true);
    }
  }

  public clearLoginBackgroundImage(): void {
    this.loginBackgroundImageFile = null;
    this.loginBackgroundImageFileName.set('');
    this.loginBackgroundImageUrl.set('');
    this.brandingForm.markAsDirty();
    this.hasUnsavedChanges.set(true);
  }

  private previewFile(file: File, type: string): void {
    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      const result = e.target?.result as string;
      if (type === 'favicon') {
        this.faviconUrl.set(result);
      } else if (type === 'logo') {
        this.logoUrl.set(result);
      } else if (type === 'loginBackgroundImage') {
        this.loginBackgroundImageUrl.set(result);
      }
    };
    reader.readAsDataURL(file);
  }

  public async saveBrandingConfig(): Promise<void> {
    if (!this.validateForm()) {
      return;
    }

    if (!this.validateRequiredAssets()) {
      return;
    }

    this.isSaving.set(true);
    this.statusMessage.set('Saving branding configuration...');

    const brandingConfig = this.buildBrandingConfiguration();

    if (!this.validateBrandingConfiguration(brandingConfig)) {
      this.isSaving.set(false);
      return;
    }

    if (this.hasFilesToUpload()) {
      this.uploadAssetsAndSave(brandingConfig);
    } else {
      this.saveBrandingConfigurationOnly(brandingConfig);
    }
  }

  private validateForm(): boolean {
    if (this.brandingForm.invalid) {
      this.statusMessage.set('Please fix validation errors before saving.');
      return false;
    }
    return true;
  }

  private validateRequiredAssets(): boolean {
    if ((!this.faviconUrl() || this.faviconUrl().trim() === '') && !this.faviconFile) {
      this.statusMessage.set('Favicon cannot be empty. Please upload a favicon image.');
      return false;
    }

    if ((!this.logoUrl() || this.logoUrl().trim() === '') && !this.logoFile) {
      this.statusMessage.set('Logo cannot be empty. Please upload a logo image.');
      return false;
    }

    return true;
  }

  private buildBrandingConfiguration(): BrandingConfiguration {
    return {
      brandingUrl: this.brandingForm.get('brandingUrl')?.value,
      themeColour: this.brandingForm.get('themeColour')?.value,
      fontFamily: this.brandingForm.get('fontFamily')?.value,
      fontColour: this.brandingForm.get('fontColour')?.value,
      faviconUrl: this.faviconUrl(),
      logoUrl: this.logoUrl(),
      loginBackgroundImageUrl: this.loginBackgroundImageUrl()
    };
  }

  private validateBrandingConfiguration(brandingConfig: BrandingConfiguration): boolean {
    const validation = this.brandingService.validateConfiguration(brandingConfig);
    if (!validation.isValid) {
      console.error('Validation failed:', validation.errors);
      this.statusMessage.set(`Validation errors: ${validation.errors.join(', ')}`);
      return false;
    }
    return true;
  }

  private hasFilesToUpload(): boolean {
    return !!(this.faviconFile || this.logoFile || this.loginBackgroundImageFile);
  }

  private uploadAssetsAndSave(_brandingConfig: BrandingConfiguration): void {
    this.statusMessage.set('Uploading branding assets...');
    this.brandingService.uploadBrandingAssets(
      this.faviconFile || undefined,
      this.logoFile || undefined,
      this.loginBackgroundImageFile || undefined)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (uploadResponse) => this.handleUploadResponse(uploadResponse as { success: boolean; message?: string; data?: { uploadedAssets?: Record<string, string> } }),
        error: (error) => this.handleUploadError(error)
      });
  }

  private handleUploadResponse(uploadResponse: { success: boolean; message?: string; data?: { uploadedAssets?: Record<string, string> } }): void {
    if (uploadResponse.success) {
      this.clearUploadedFiles();
      this.updateUrlsFromUploadResponse(uploadResponse);
      const updatedBrandingConfig = this.buildBrandingConfiguration();
      this.saveBrandingConfigurationOnly(updatedBrandingConfig);
    } else {
      this.statusMessage.set(`Asset upload failed: ${uploadResponse.message}`);
      this.isSaving.set(false);
    }
  }

  private clearUploadedFiles(): void {
    this.faviconFile = null;
    this.logoFile = null;
    this.loginBackgroundImageFile = null;
  }

  private updateUrlsFromUploadResponse(uploadResponse: { data?: { uploadedAssets?: Record<string, string> } }): void {
    if (uploadResponse.data?.uploadedAssets) {
      const assets = uploadResponse.data.uploadedAssets;
      if (assets['favicon']) {
        this.faviconUrl.set(assets['favicon']);
      }
      if (assets['logo']) {
        this.logoUrl.set(assets['logo']);
      }
      if (assets['loginBackgroundImage']) {
        this.loginBackgroundImageUrl.set(assets['loginBackgroundImage']);
      }
    }
  }

  private handleUploadError(error: unknown): void {
    console.error('Error uploading branding assets:', error);
    this.statusMessage.set('Error uploading branding assets. Please try again.');
    this.isSaving.set(false);
  }

  private saveBrandingConfigurationOnly(brandingConfig: BrandingConfiguration): void {
    this.statusMessage.set('Saving branding configuration...');
    this.brandingService.saveBrandingConfiguration(brandingConfig)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          if (response.success) {
            const savedConfig = response.data ?? brandingConfig;
            // Apply the new theme
            this.brandingService.applyBrandingConfiguration(savedConfig);
            this.themingService.apply();
            this.patchFormWithConfig(savedConfig);

            this.brandingForm.markAsPristine();
            this.hasUnsavedChanges.set(false);
            this.statusMessage.set(`${response.message}`);
          } else {
            this.statusMessage.set(`${response.message}`);
          }
          this.isSaving.set(false);
        },
        error: (error) => {
          console.error('Error saving branding configuration:', error);
          this.statusMessage.set('Error saving branding configuration. Please try again.');
          this.isSaving.set(false);
        }
      });
  }

  public getFormControlError(controlName: string): string | null {
    const control = this.brandingForm.get(controlName);
    if (!control?.errors || !control?.touched) {
      return null;
    }

    if (control.errors['required']) {
      return control.errors['required'].message || 'This field is required.';
    }
    if (control.errors['pattern']) {
      return control.errors['pattern'].message || 'Invalid format.';
    }
    if (control.errors['invalidColor']) {
      return control.errors['invalidColor'].message || 'Invalid color format.';
    }
    if (control.errors['invalidFontFamily']) {
      return control.errors['invalidFontFamily'].message || 'Invalid font family format.';
    }

    return 'Invalid value.';
  }
}
